"use client";

import { useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";

import { useListSequences } from "@/lib/api/generated";
import type { ListSequences200 } from "@/lib/api/model/listSequences200";
import type { ListSequencesParams } from "@/lib/api/model/listSequencesParams";

/**
 * Slim sequence row returned by `GET /api/jobs/{job_id}/sequences`.
 *
 * The Orval-generated `ListSequences200` type is a generic dict because the
 * backend route's `response_model=dict[str, Any]` (see
 * `backend/app/api/routes/sequences.py::list_sequences`). The actual payload
 * is `{ sequences: SequenceListItem[], next_cursor: string | null }`. This
 * narrow type re-asserts the shape so the list/table can stay strongly typed
 * without forcing a backend regen + Orval re-run for a slim envelope.
 */
export interface SequenceListSender {
  id: string;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
}

export interface SequenceListAnalytics {
  total: number;
  in_progress: number;
  finished: number;
  opened: number;
  clicked: number;
  replied: number;
  interested: number;
  bounced: number;
}

/** Why a sequence can't take new enrollments right now (server-computed).
 * `null`/absent = enrollable. Mirrors the backend
 * `SequenceEnrollmentBlockReason`. */
export type SequenceEnrollmentBlockReason =
  | "mailbox_missing"
  | "mailbox_disconnected"
  | "mailbox_needs_reconnect"
  | "unconfigured_steps";

export interface SequenceListItem {
  id: string;
  org_id: string;
  project_id: string;
  job_id: string;
  name: string;
  description?: string | null;
  sender_user_id: string;
  mailbox_id: string;
  is_archived: boolean;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  step_count: number;
  /** Steps in the current step set still missing required content. > 0
   * means the sequence can't accept new enrollments until configured. */
  unconfigured_step_count?: number;
  /** Why this sequence can't take new enrollments (sender mailbox state or
   * unconfigured steps). `null`/absent = enrollable. */
  enrollment_blocked_reason?: SequenceEnrollmentBlockReason | null;
  sender: SequenceListSender | null;
  analytics: SequenceListAnalytics;
}

interface SequencesPayload {
  sequences: SequenceListItem[];
  next_cursor: string | null;
}

function asPayload(raw: ListSequences200 | undefined): SequencesPayload {
  if (!raw || typeof raw !== "object") {
    return { sequences: [], next_cursor: null };
  }
  const sequences = Array.isArray((raw as { sequences?: unknown }).sequences)
    ? ((raw as { sequences: SequenceListItem[] }).sequences ?? [])
    : [];
  const next_cursor = ((raw as { next_cursor?: unknown }).next_cursor ?? null) as
    | string
    | null;
  return { sequences, next_cursor };
}

interface UseSequencesOptions {
  jobId: string | null | undefined;
  params?: Omit<ListSequencesParams, never>;
}

/**
 * Convenience wrapper around `useListSequences` that:
 *  - Disables the query until a real `jobId` is present.
 *  - Reshapes the dict response into a typed `{ sequences, next_cursor }`.
 *  - Defaults sort by leaving the backend's `updated_at DESC` order intact.
 */
export function useSequences({ jobId, params }: UseSequencesOptions) {
  const query = useListSequences(jobId ?? "", params, {
    query: { enabled: !!jobId, placeholderData: keepPreviousData },
  });

  const payload = useMemo(() => asPayload(query.data), [query.data]);

  return {
    sequences: payload.sequences,
    nextCursor: payload.next_cursor,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    queryKey: query.queryKey,
  };
}
